import { useState } from "react";
import { BROWSABLE_CATEGORIES, CATEGORIES, ROUTES } from "@oshimap/config";
import { normalizeCategoryCode } from "@oshimap/domain";
import { ep, session } from "../../lib/api";
import { useQuery } from "../../lib/use-query";
import { navigate } from "../../app/router";
import { Button, CategoryChip, Chip, EmptyState, ErrorState, Skeleton, CategoryIcon } from "../../components/ui";
import "./explore.css";
import { recentSearches, rememberSearch, forgetSearches } from "./recent-searches";
import { queryKeys } from "@oshimap/query";

/** FR-EXP-05: 결과 없음과 빈 상태를 구분해서 표현한다. */
export function SearchScreen({ initialQuery }: { initialQuery?: string }) {
  const [term, setTerm] = useState(initialQuery ?? "");
  const [submitted, setSubmitted] = useState(initialQuery ?? "");
  /* 실제로 이 기기에서 찾은 것만. 서버에 보내지 않는다.
     사람마다 따로 둔다 — 한 기기를 나눠 써도 앞사람이 무엇을 찾았는지가
     내 "최근 검색" 에 올라오지 않는다. */
  const me = session.user?.id ?? null;
  const [recent, setRecent] = useState<string[]>(() => recentSearches(me));

  const { state, reload } = useQuery(
    queryKeys.search(submitted, { pageSize: 40 }),
    async (signal) => (submitted ? ep.listPlaces({ search: submitted, pageSize: 40 }, { signal }) : []),
  );

  const run = (q: string) => {
    setTerm(q);
    setSubmitted(q);
    if (q) setRecent(rememberSearch(q, me));
    navigate(`${ROUTES.search}?q=${encodeURIComponent(q)}`, { replace: true });
  };

  return (
    <div className="om-screen om-explore">
      <h1 className="sr-only">검색</h1>
      <header className="om-explore__head">
        <button className="om-back" onClick={() => navigate(ROUTES.explore)} aria-label="탐색으로">‹</button>
        <form className="om-searchform" onSubmit={(e) => { e.preventDefault(); run(term.trim()); }}>
          <label htmlFor="om-q" className="sr-only">검색어</label>
          <input id="om-q" className="om-searchinput" value={term} placeholder="장소 · 이벤트 · 작품"
            onChange={(e) => setTerm(e.target.value)} autoComplete="off" />
          {term ? <button type="button" className="om-clear" onClick={() => { setTerm(""); setSubmitted(""); }} aria-label="지우기">×</button> : null}
        </form>
      </header>

      <div className="om-explore__body">
        {!submitted ? (
          <>
            {recent.length > 0 ? (
              <>
                <div className="om-search__head">
                  <h2 className="om-section__title" style={{ padding: 0 }}>최근 검색</h2>
                  <Button variant="ghost" size="sm"
                    onClick={() => { forgetSearches(me); setRecent([]); }}>지우기</Button>
                </div>
                <div className="om-chiprow">
                  {recent.map((q) => <Chip key={q} onClick={() => run(q)}>{q}</Chip>)}
                </div>
              </>
            ) : null}

            {/*
              * 예전에는 "지금 많이 찾는" 이라며 고정 문자열을 보여줬다.
              * 그런 데이터가 서버에 없다 — 없는 걸 있는 척하지 않는다.
              * 대신 실제로 있는 것(카테고리)으로 첫 걸음을 만든다.
              */}
            <h2 className="om-section__title" style={{ padding: 0, marginTop: recent.length ? "var(--s-6)" : 0 }}>
              무엇을 찾으시나요?
            </h2>
            <div className="om-chiprow">
              {BROWSABLE_CATEGORIES.map((c) => (
                <Chip key={c.code} onClick={() => navigate(ROUTES.exploreCategory(c.code))}>
                  <CategoryIcon code={c.code} size={17} />{c.label}
                </Chip>
              ))}
            </div>
          </>
        ) : state.status === "loading" || state.status === "idle" ? (
          <Skeleton lines={3} />
        ) : state.status === "error" ? (
          <ErrorState title="검색하지 못했어요" body={state.error.message} onRetry={reload} />
        ) : state.data.length === 0 ? (
          <>
            <EmptyState
              title={`‘${submitted}’ 결과가 없어요`}
              body="아직 등록되지 않았을 수 있어요. 알고 계신 곳이라면 제보해 주세요."
              action={
                <div className="om-chiprow" style={{ justifyContent: "center" }}>
                  <Button variant="secondary" size="sm" onClick={() => { setTerm(""); setSubmitted(""); }}>다른 키워드로</Button>
                  <Button variant="primary" size="sm" onClick={() => navigate(ROUTES.submit)}>제보하기</Button>
                </div>
              }
            />
            {/* "이런 곳은 어때요" 로 아무 말이나 던지지 않는다. 실제로 있는 카테고리로 안내한다. */}
            <h2 className="om-section__title" style={{ padding: 0, marginTop: "var(--s-6)" }}>카테고리로 찾아보기</h2>
            <div className="om-chiprow">
              {BROWSABLE_CATEGORIES.slice(0, 4).map((c) => (
                <Chip key={c.code} onClick={() => navigate(ROUTES.exploreCategory(c.code))}>
                  <CategoryIcon code={c.code} size={17} />{c.label}
                </Chip>
              ))}
            </div>
          </>
        ) : (
          <>
            <p className="om-count num">{state.data.length}곳</p>
            <div className="om-cards" style={{ padding: 0 }}>
              {state.data.map((p) => (
                <button key={p.id} className="om-card" onClick={() => navigate(ROUTES.place(p.id))}>
                  <span className="om-card__thumb"><CategoryIcon code={p.category.code} size={26} /></span>
                  <span className="om-card__body">
                    <span className="om-card__name">{p.name}</span>
                    <span className="om-card__row"><CategoryChip code={p.category.code} /></span>
                  </span>
                </button>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
